import { reducedMotion } from './motion'

// Pull down to refresh (Liam, 2026-09-24). Opened from the home screen,
// Roundly runs as its own app with no browser around it: there was no way to
// reload it, and so no way to pick up a new version short of swiping it away
// in the app switcher. Pull down from the top of any screen, past the line,
// and let go: the page loads again. In an ordinary browser tab the browser
// already does this, so it is left to the browser there.
//
// Never over work in progress: not while a panel is open, a voice note is
// recording, a photo waits for its label, or a reason or late text is being
// chosen. Nothing else is at risk: saves
// waiting for signal, and photos and voice notes not yet sent, are kept on
// the phone and carry on after the reload.

const PULL_AT = 80
const MAX_PULL = 130

function standalone(): boolean {
  // Roundly's iPhone app (ios/) has no browser round it either.
  if (typeof window !== 'undefined' && 'RoundlyIOS' in window) return true
  return (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) || (navigator as { standalone?: boolean }).standalone === true
}

function busy(): boolean {
  return !!document.querySelector('dialog[open], .voice-recording, .photo-pick:not([hidden]), .slide-dragging, .blocked-chooser, .late-chooser')
}

// The Diary's Due bar and its phone sheet have their own drag down (Liam, 20 Oct 2026: "this can be dragged down which
// is great but i can also do it in a way accidentally that refreshes the page when dragging down"), so a pull never
// starts on them.
const NO_PULL = '.day-strip, .slide-track, input, textarea, select, dialog, .live-more-list, .photo-overlay, .diary-due-bar, .diary-sheet'

// Another gesture has the touch (Astra's review, 20 Oct 2026): a Diary row, a Due card, a Month visit or a held card
// being moved. Each says so on the page while it is held, and each stops the page moving under it (preventDefault),
// so a pull that began before it took the finger is let go.
function taken(): boolean {
  const body = document.body.classList
  return body.contains('diary-row-dragging') || body.contains('diary-due-dragging')
    || !!document.querySelector('.row-drag-held, .month-drag-layer, .hold-drag-lifted, .diary-due-chip.is-pushing, .diary-due-chip.is-lifted')
}

export function enablePullToRefresh(): void {
  if (!standalone()) return
  const mark = document.createElement('div')
  mark.className = 'pull-mark'
  mark.setAttribute('aria-hidden', 'true')
  document.body.append(mark)
  let startY: number | null = null
  let pull = 0

  const paint = (): void => {
    const k = Math.min(1, pull / PULL_AT)
    mark.style.transform = `translate(-50%, ${Math.min(pull, MAX_PULL) * 0.6}px) rotate(${k * 300}deg)`
    mark.style.opacity = String(k)
    mark.classList.toggle('pull-ready', pull >= PULL_AT)
  }
  const reset = (): void => {
    startY = null
    pull = 0
    mark.classList.add('pull-back')
    paint()
    window.setTimeout(() => mark.classList.remove('pull-back'), 250)
  }

  // A pull is armed only by one finger landing at the top, away from anything with its own drag; any new touch starts
  // again from nothing, so a second finger, or a start on such a part, lets an armed pull go (Astra's review, 20 Oct 2026).
  document.addEventListener(
    'touchstart',
    (e) => {
      if (startY !== null) reset()
      if (window.scrollY > 0 || e.touches.length !== 1 || busy() || taken()) return
      if ((e.target as Element | null)?.closest(NO_PULL)) return
      startY = e.touches[0].clientY
      pull = 0
    },
    { passive: true },
  )
  document.addEventListener(
    'touchmove',
    (e) => {
      if (startY === null) return
      // Scrolled, a second finger, or the move taken by another gesture: not a pull any more.
      if (window.scrollY > 0 || e.touches.length !== 1 || e.defaultPrevented || taken()) {
        reset()
        return
      }
      pull = Math.max(0, e.touches[0].clientY - startY)
      paint()
    },
    { passive: true },
  )
  document.addEventListener('touchend', () => {
    if (startY === null) return
    // Asked again as the finger lifts: nothing open, no other gesture holding the touch.
    if (pull >= PULL_AT && !busy() && !taken()) {
      mark.classList.add('pull-going')
      window.setTimeout(() => location.reload(), reducedMotion() ? 0 : 200)
      return
    }
    reset()
  })
  document.addEventListener('touchcancel', () => {
    if (startY !== null) reset()
  })
}
